import React, { forwardRef, useContext, useEffect, useState } from 'react'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import * as yup from 'yup'
import AdxInput from 'components/custom/AdxInput'
import Box from '@mui/system/Box'
import InputAdornment from '@mui/material/InputAdornment'
import AdxDatePicker from 'components/custom/AdxDatePicker'
import { Typography } from '@mui/material'
import { parse, set } from 'date-fns'
import AdxDropzoneImagePdf from 'components/reusable/AdxDropzoneImagePdf'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import { ExpenseContext } from '../context/ExpenseContext'
import AdxButton from 'components/custom/AdxButton'

const ReceiptExpenseForm = forwardRef((props, ref) => {
  const {
    category = '',
    disabled = true,
    categoryAllocation,
    expenseMutate,
    expenseData,
    reportData,
  } = props

  // reset today's time to 12am
  const { start_date, end_date } = reportData
  const today = set(new Date(), {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  })

  // form validation schema
  const validationSchema = yup.object({
    claim_expense_category_id: yup.string().required('Category is required'),
    description: yup.string().required('Description is required'),
    date: yup
      .date()
      .required('Receipt date is required')
      .max(today, 'Receipt cannot be in the future'),
    merchant: yup.string().required('Merchant is required'),
    amount: yup
      .number()
      .required('Amount is required')
      .positive('Invalid amount')
      .min(0.01, 'Invalid amount')
      .max(categoryAllocation ? categoryAllocation : Infinity, 'Your request is overlimit'),
    proof_link: yup
      .string()
      .trim()
      .max(2048, 'Proof link must be 2048 characters or less')
      .url('Proof link must be a valid URL')
      .nullable()
      .notRequired(),
    file: yup.mixed().required('Receipt is required'),
  })

  // set category id when dropdown change
  useEffect(() => {
    ref.current.setFieldValue('claim_expense_category_id', category)
  }, [category])

  // update the context every 100 miliseconds
  const { savedExpenseSession, setExpenseSession } = useContext(ExpenseContext)
  const [time, setTime] = useState(Date.now())

  useEffect(() => {
    if (!disabled && !expenseData) {
      const interval = setInterval(() => setTime(Date.now()), 100)
      return () => {
        clearInterval(interval)
      }
    }
  }, [disabled])
  useEffect(() => {
    if (!disabled && !expenseData) {
      setExpenseSession({ ...ref.current.values })
    }
  }, [time])

  return (
    <Formik
      innerRef={ref}
      initialValues={{
        claim_expense_category_id: category,
        description: expenseData?.description ? expenseData?.description : '',
        date: expenseData?.date ? expenseData?.date : '',
        merchant: expenseData?.merchant ? expenseData?.merchant : '',
        amount: expenseData?.amount ? expenseData?.amount : '',
        proof_link: expenseData?.proof_link ? expenseData?.proof_link : '',
        file: expenseData?.file ? expenseData?.file?.name : '',
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const { file } = values
        const newValues = {
          ...values,
          proof_link: values.proof_link?.trim() || null,
          attachment: file.name || file,
        }
        expenseMutate(newValues)
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
        return (
          <Form>
            <Box width={1} boxSizing="border-box" display="flex" flexDirection="column" rowGap={1}>
              <Field
                disabled={disabled}
                name="description"
                as={AdxInput}
                multiline
                minRows={3}
                sxInput={{ padding: 2 }}
                charLimit={120}
                sx={{ width: 1 }}
                label="Description"
                onChange={handleChange}
                onBlur={handleBlur}
                errorMessage={<ErrorMessage name="description" component={AdxErrorMessage} />}
                isErrorShown={errors.description && touched.description}
              />
              <Field
                disabled={disabled}
                name="merchant"
                as={AdxInput}
                label="Merchant"
                charLimit={50}
                onChange={handleChange}
                onBlur={handleBlur}
                errorMessage={<ErrorMessage name="merchant" component={AdxErrorMessage} />}
                isErrorShown={errors.merchant && touched.merchant}
              />
              <Box
                display={{ xs: 'block', lg: 'flex' }}
                columnGap={3}
                mt={{ xs: categoryAllocation && 1, lg: 0 }}
              >
                <Field
                  disabled={disabled}
                  name="amount"
                  as={AdxInput}
                  label="Amount"
                  subLabel={
                    !disabled && categoryAllocation !== null
                      ? `Balance RM ${parseFloat(categoryAllocation).toFixed(2)}`
                      : null
                  }
                  type="number"
                  sx={{ width: { xs: 1, lg: 1 / 2 } }}
                  inputProps={{
                    min: '0.00',
                    max: categoryAllocation ? categoryAllocation : '999999.99',
                    step: '0.01',
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <Typography color={disabled ? 'gray-inactive-button.main' : 'dark-text.main'}>
                        RM
                      </Typography>
                    </InputAdornment>
                  }
                  endAdornment={
                    !disabled && categoryAllocation !== null ? (
                      <InputAdornment position="end">
                        <AdxButton
                          variant={'outlined'}
                          disabled={disabled || categoryAllocation === 0}
                          sx={{
                            height: 0,
                            minWidth: 0,
                            paddingX: 1,
                            paddingY: 1.5,
                          }}
                          size="small"
                          onClick={(e) => setFieldValue('amount', categoryAllocation)}
                        >
                          MAX
                        </AdxButton>
                      </InputAdornment>
                    ) : null
                  }
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e)
                    const value = parseFloat(e.target.value).toFixed(2)
                    if (categoryAllocation !== null) {
                      if (value > categoryAllocation) {
                        setFieldValue('amount', categoryAllocation)
                      } else if (e.target.value === '') {
                        setFieldValue('amount', '')
                      } else {
                        setFieldValue('amount', parseFloat(value).toFixed(2))
                      }
                    }
                  }}
                  errorMessage={<ErrorMessage name="amount" component={AdxErrorMessage} />}
                  isErrorShown={errors.amount && touched.amount}
                />

                <Field
                  disabled={disabled}
                  name="date"
                  as={AdxDatePicker}
                  sx={{ width: { xs: 1, lg: 1 / 2 } }}
                  label="Receipt date"
                  onChange={(e) => {
                    if (e === null) {
                      setFieldValue('date', '')
                    } else {
                      setFieldValue(
                        'date',
                        set(e, {
                          hours: 0,
                          minutes: 0,
                          seconds: 0,
                          milliseconds: 0,
                        })
                      )
                    }
                  }}
                  onBlur={handleBlur}
                  minDate={parse(start_date, 'dd/MM/yyyy', new Date())}
                  maxDate={parse(end_date, 'dd/MM/yyyy', new Date())}
                  errorMessage={<ErrorMessage name="date" component={AdxErrorMessage} />}
                  isErrorShown={errors.date && touched.date}
                />
              </Box>
              <Field
                disabled={disabled}
                name="proof_link"
                as={AdxInput}
                label="Proof link"
                subLabel="Optional"
                placeholder="Paste Google Maps or site-study link"
                charLimit={2048}
                onChange={handleChange}
                onBlur={handleBlur}
                errorMessage={<ErrorMessage name="proof_link" component={AdxErrorMessage} />}
                isErrorShown={errors.proof_link && touched.proof_link}
              />
              <Field name="file" type="hidden" />
              <AdxDropzoneImagePdf
                label="Attachment"
                title="Upload receipt from computer"
                subtitle="or click here to submit claim"
                folder="claim_expenses"
                setFieldValue={setFieldValue}
                file={
                  savedExpenseSession.resumeStatus && savedExpenseSession?.file
                    ? {
                        url: savedExpenseSession?.file?.awsUrl,
                        name: savedExpenseSession?.file?.name,
                      }
                    : expenseData?.file
                }
                disabled={disabled}
                errorMessage={<ErrorMessage name="file" component={AdxErrorMessage} />}
                isErrorShown={errors.file && touched.file}
                parentsDependencies={[disabled]}
                onReplace={() => delete savedExpenseSession.file}
              />

              <button type="submit" hidden>
                Submit
              </button>
            </Box>
          </Form>
        )
      }}
    </Formik>
  )
})

export default ReceiptExpenseForm
